/* eslint-disable no-plusplus */
import { GridColDef } from '@mui/x-data-grid-pro';
import { IRecordField, IRecordFieldsList, IRecordHeaders } from 'ui-component/records/types';
import { DEFAULT_HIDDEN_FIELDS } from '../generateGridState';

/**
 * Return an object with the base fields and additional fields splitted
 * and validated with given not allowed function
 *
 * @param headers {IRecordFieldList}
 * @param headers {IRecordFieldList}
 */
export const splitHeaders = (headers: IRecordFieldsList | null, filterNotAllowedFn?: (key: string) => boolean) => {
    const baseFieldHeaders: Partial<IRecordFieldsList> = {};
    const additionalFieldHeaders: Partial<IRecordFieldsList> = {};
    if (headers)
        for (const fieldName in headers) {
            if (Object.prototype.hasOwnProperty.call(headers, fieldName)) {
                const element = headers[fieldName];
                if (!filterNotAllowedFn || (filterNotAllowedFn(fieldName) && element.gridEditable))
                    if (Number(element.id) === 0) baseFieldHeaders[fieldName] = headers[fieldName];
                    else additionalFieldHeaders[fieldName] = headers[fieldName];
            }
        }

    return { baseFieldHeaders, additionalFieldHeaders };
};

/**
 * Returns and object with all the changes of the record
 *
 * @param oldData {Object} actual data of the record
 * @param newData {Object} new data of the record
 */
export const generateClearedRecordData = (
    oldData: Record<string, any>,
    newData: Record<string, number | boolean | string>,
    objectValues: Record<string, any>
) => {
    const clearedData: Record<string, any> = {};

    for (const fieldName in newData) {
        if (Object.prototype.hasOwnProperty.call(newData, fieldName)) {
            if (!Object.prototype.hasOwnProperty.call(objectValues, fieldName)) {
                const oldValue = oldData[fieldName];
                const newValue = newData[fieldName];
                const newValueExist = verifyNewValue(newValue);
                if ((!oldValue && newValueExist) || (oldValue && String(oldValue) !== String(newValue))) {
                    clearedData[fieldName] = newValue;
                }
            }
        }
    }

    return clearedData;
};

/**
 * Check if the new value is valid, returns true
 * when exist and have length in string cases
 *
 * @param newVal
 */
const verifyNewValue = (newVal: any) => {
    // TODO: write some tests for number case here
    // TODO: remove currency
    if (['object', 'boolean', 'number'].includes(typeof newVal)) return true;
    if (typeof newVal === 'string') return !!newVal.length;
    return false;
};

/**
 * Returns the ids of list that exists in headers
 *
 * @param headers {IRecordFieldsList}
 */
export const getListIdsFromHeaders = (headers?: Partial<IRecordFieldsList>) => {
    if (!headers || !Object.keys(headers).length) return [];
    const ids: number[] = [];

    for (const key in headers) {
        if (Object.prototype.hasOwnProperty.call(headers, key)) {
            const element = headers[key];
            if (element?.listType?.id) ids.push(Number(element.listType.id));
        }
    }

    return ids;
};

export const getListValueIdsFromData = (headers?: Partial<IRecordFieldsList>, list?: IRecordHeaders[]) => {
    if (!headers || !list?.length) return [];
    const headersWithListType = Object.fromEntries(
        Object.entries(headers).filter(
            ([_key, val]) => val?.listType?.id && !['multiselect', 'multiselect-checkbox'].includes(val?.dataType)
        )
    );
    const headersWithListTypeIds = Object.values(headersWithListType).map((val) => +(val as IRecordField).id);

    const ids: number[] = list.reduce<number[]>((acc, { additionalFields }) => {
        const valueIds: number[] = [];
        for (const { tag, value } of additionalFields) {
            const [, , fieldId] = tag.split(';');
            let formattedValue: number[] = [];

            try {
                const parsedVal = JSON.parse(value);
                const valueIsArray = Array.isArray(parsedVal);
                if (valueIsArray) {
                    formattedValue = parsedVal.map(Number); // Convertir cada elemento a número
                } else {
                    formattedValue = [Number(parsedVal)]; // Convertir el valor parseado a número
                }
            } catch (err) {
                formattedValue = [Number(value)]; // Convertir el valor string a número
            }

            if (headersWithListTypeIds.includes(+fieldId) && formattedValue) {
                valueIds.push(...formattedValue);
            }
        }
        return [...acc, ...valueIds];
    }, []);

    return ids;
};

/**
 * Changes the index of an item in an array.
 *
 * @param {any[]} arr - The array to modify.
 * @param {number} oldIndex - The current index of the item.
 * @param {number} newIndex - The new index to move the item to.
 * @return {any[]} The modified array.
 */
function changeArrayItemIndex(arr: any[], oldIndex: number, newIndex: number) {
    if (newIndex >= arr.length) {
        let marker = newIndex - arr.length + 1;
        while (marker--) {
            arr.push(undefined);
        }
    }
    arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
    return arr;
}

/**
 * Sorts the columns based on header names or field names and returns an array of fields.
 *
 * @param {GridColDef[]} columns - The array of GridColDef columns to be sorted.
 * @return {string[]} An array of field names sorted based on header names or field names.
 */
export const getTogglableColumns = (columns: GridColDef[]) => {
    // Needs the columns references to work
    columns.sort((a, b) => {
        // Validate record fields and DataGrid default columns
        const compareA = a.headerName && a.headerName.length > 0 ? a.headerName?.toLowerCase() : a.field.toLowerCase();
        const compareB = b.headerName && b.headerName.length > 0 ? b.headerName?.toLowerCase() : b.field.toLowerCase();
        return compareA.localeCompare(compareB);
    });
    // Search for the action and checkbox column
    const actionColumn = columns.find((column) => column.field === 'actions' && column.headerName === '');
    const checkboxSelectionColumn = columns.find((column) => column.field === '__check__' && column.headerName === 'Checkbox selection');
    // Move them to the firsts positions ignoring the alphabetical order
    if (actionColumn) changeArrayItemIndex(columns, columns.indexOf(actionColumn), 0);
    if (checkboxSelectionColumn) changeArrayItemIndex(columns, columns.indexOf(checkboxSelectionColumn), 1);
    // Return the fields only
    return columns.map((column) => column.field).filter((column) => !DEFAULT_HIDDEN_FIELDS.includes(column));
};

const HEADER_NAME_MAPPING: { [key: string]: string } = {
    createdAt: 'createdOn',
    receivedAt: 'receivedOn',
    updatedAt: 'updatedOn',
    approvedAt: 'approvedOn'
};
export const adjustHeaders = (name: string) => HEADER_NAME_MAPPING[name] || name;
